import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loading / error / data for one endpoint, with optional silent polling.
 *
 * - The dep-triggered load shows the loading state.
 * - Poll ticks revalidate silently: a successful tick swaps the data with no
 *   flicker, a failed tick surfaces `staleError` while the last good data stays
 *   on screen.
 * - Both effects abort their in-flight request and clear their timer on
 *   unmount, so nothing leaks when you navigate away mid-request.
 */
export function useResource(load, deps = [], options = {}) {
  const { intervalMs = 0, paused = false, enabled = true } = options;

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  const [state, setState] = useState({
    status: enabled ? 'loading' : 'idle',
    data: null,
    error: null,
    staleError: null,
    updatedAt: null,
  });

  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const depKey = JSON.stringify(deps);

  // Primary load.
  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle', data: null, error: null, staleError: null, updatedAt: null });
      return undefined;
    }

    let alive = true;
    const controller = new AbortController();

    setState((prev) => ({ ...prev, status: 'loading', error: null, staleError: null }));

    loadRef
      .current(controller.signal)
      .then((data) => {
        if (!alive) return;
        setState({
          status: 'ready',
          data,
          error: null,
          staleError: null,
          updatedAt: Date.now(),
        });
      })
      .catch((error) => {
        if (!alive || error?.name === 'AbortError') return;
        setState({ status: 'error', data: null, error, staleError: null, updatedAt: null });
      });

    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey, nonce, enabled]);

  // Silent revalidation.
  useEffect(() => {
    if (!enabled || !intervalMs || paused) return undefined;

    let alive = true;
    let controller = null;

    const timer = setInterval(() => {
      controller = new AbortController();
      loadRef
        .current(controller.signal)
        .then((data) => {
          if (!alive) return;
          setState({
            status: 'ready',
            data,
            error: null,
            staleError: null,
            updatedAt: Date.now(),
          });
        })
        .catch((error) => {
          if (!alive || error?.name === 'AbortError') return;
          setState((prev) =>
            prev.status === 'ready'
              ? { ...prev, staleError: error }
              : { status: 'error', data: null, error, staleError: null, updatedAt: null },
          );
        });
    }, intervalMs);

    return () => {
      alive = false;
      clearInterval(timer);
      if (controller) controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, paused, depKey, nonce]);

  return { ...state, refresh };
}
